import { useEffect, useState } from 'react';

import { AlgorithmStatusType } from '~/enums/AlgorithmStatusType';
import { UploadStatusType } from '~/enums/UploadStatusType';
import { api } from '~/redux/api/createApi';
import { Entity } from '~/types/Entity';
import { Nullable } from '~/types/Nullable';

export const useDocumentProcessingStatus = (
  entity: Nullable<Entity<'document_handler' | 'ticket'>>,
): {
  isLoading: boolean;
  isProcessing: boolean;
  status?: UploadStatusType;
  isOcrProcessing: boolean;
} => {
  const { id, type } = entity ?? {};

  const [isProcessing, setIsProcessing] = useState<boolean | undefined>();

  useEffect(() => {
    if (!entity) {
      setIsProcessing(undefined);
    }

    return () => {
      setIsProcessing(undefined);
    };
  }, [entity]);

  const shouldStopFetching = isProcessing === false;

  const endpoint =
    type === 'document_handler' ? 'getDocumentAlgoStatus' : 'getDocumentByTag';

  const { isLoading: isDocumentAlgoStatusLoading, data: algoStatusData } =
    api.endpoints[endpoint].useQuery(
      { id: Number(id), tag: id as string },
      {
        skip: !entity || shouldStopFetching,
        pollingInterval: 5000,
      },
    );

  useEffect(() => {
    if (algoStatusData) {
      setIsProcessing(
        [UploadStatusType.Processing, UploadStatusType.Queued].includes(
          algoStatusData.processingStatus,
        ),
      );
    }
  }, [algoStatusData]);

  return {
    status: algoStatusData?.processingStatus,
    isLoading: isDocumentAlgoStatusLoading,
    isProcessing: Boolean(isProcessing),
    isOcrProcessing:
      algoStatusData?.algoStatus.ocr.status === AlgorithmStatusType.InProgress,
  };
};
